import { apiClient, idempotentConfig, unwrap } from './client';
import type { ApiEnvelope, UamUserView } from './types';

/**
 * IAM UC2 — Team & Access (OPSFL-FSD-IAM-UC2 §12 slice 7): custom roles (§5), projects (§4),
 * delegations (§7) and ABAC policies (§6). A thin contract-faithful transport like uam.ts —
 * every mutation carries an `ol-idempotency-key`; THE SERVER IS THE GATE (the console only hides
 * affordances; an unhidden affordance still 403s server-side — the §12 tamper contract).
 *
 * Server-authored refusals the panels render (ApiError.code):
 *   roles:       422 ROLE_KEY_UNKNOWN / ROLE_KEY_NOT_BINDABLE / ROLE_KEY_EXCEEDS_AUTHOR /
 *                ROLE_LIMIT_EXCEEDED
 *   delegations: 422 DELEGATION_SELF_FORBIDDEN (+ scope/ceiling refusals when EXERCISED)
 *   abac:        422 ABAC_VIEW_KEY_REFUSED / ABAC_KEY_NOT_ELIGIBLE / ABAC_POLICY_INVALID
 *   acl seam:    422 ACL_PROJECT_UNKNOWN (PROJECT grantees, slice 3)
 */

// ---- custom roles (§5) ------------------------------------------------------------------------

export interface CustomRole {
  roleId: string;
  roleKey: string;
  displayName: string;
  status: 'DRAFT' | 'ACTIVE' | 'RETIRED';
  scopeResourceType: string | null;
  version: number;
  permissionKeys: string[];
  createdBy: string;
  createdAt: string | null;
  retiredBy: string | null;
  retiredAt: string | null;
}

export async function listRoles(status?: string): Promise<CustomRole[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ roles: CustomRole[] }>>('/v1/roles', {
    params: status ? { status } : {},
  });
  const body = unwrap(data);
  return Array.isArray(body?.roles) ? body.roles : [];
}

export async function createRole(req: {
  roleKey: string;
  displayName: string;
  permissionKeys: string[];
  scopeResourceType?: string | null;
}): Promise<CustomRole> {
  const { data } = await apiClient.post<ApiEnvelope<CustomRole>>('/v1/roles', req,
    idempotentConfig());
  return unwrap(data);
}

export async function getRole(roleId: string): Promise<CustomRole> {
  const { data } = await apiClient.get<ApiEnvelope<CustomRole>>(
    `/v1/roles/${encodeURIComponent(roleId)}`);
  return unwrap(data);
}

/**
 * PUT /v1/roles/{id} — display name + full key set + scope clause, in one replacement (the server
 * revokes-and-adds key rows, I7; the scope clause reconciles to exactly one live CONFINE row).
 * Same §5.4 guards as create: ROLE_KEY_UNKNOWN / NOT_BINDABLE / EXCEEDS_AUTHOR.
 */
export async function updateRole(roleId: string, req: {
  displayName: string;
  permissionKeys: string[];
  scopeResourceType?: string | null;
}): Promise<CustomRole> {
  const { data } = await apiClient.put<ApiEnvelope<CustomRole>>(
    `/v1/roles/${encodeURIComponent(roleId)}`, req, idempotentConfig());
  return unwrap(data);
}

export async function retireRole(roleId: string): Promise<CustomRole> {
  const { data } = await apiClient.post<ApiEnvelope<CustomRole>>(
    `/v1/roles/${encodeURIComponent(roleId)}/retire`, undefined, idempotentConfig());
  return unwrap(data);
}

/**
 * GET /v1/roles/{id}/holders — the directory users currently holding the role (read-through to
 * UAM; 503 UAM_DIRECTORY_UNAVAILABLE when the identity seam is absent — render that honestly).
 */
export async function listRoleHolders(roleId: string): Promise<{
  roleKey: string;
  holders: UamUserView[];
}> {
  const { data } = await apiClient.get<ApiEnvelope<{ roleKey: string; holders: UamUserView[] }>>(
    `/v1/roles/${encodeURIComponent(roleId)}/holders`);
  const body = unwrap(data);
  return { roleKey: body?.roleKey ?? '', holders: Array.isArray(body?.holders) ? body.holders : [] };
}

// ---- projects (§4) ----------------------------------------------------------------------------

export interface Project {
  projectId: string;
  projectKey: string;
  name: string;
  spokeIdPin: string | null;
  status: 'ACTIVE' | 'CLOSED';
  createdBy: string;
  createdAt: string | null;
  closedBy: string | null;
  closedAt: string | null;
}

export interface ProjectMember {
  memberType: 'USER' | 'ROLE';
  member: string;
  projectRole: 'LEAD' | 'MEMBER' | 'VIEWER';
  addedBy: string;
  addedAt: string | null;
  expiresAt: string | null;
  revokedBy: string | null;
  revokedAt: string | null;
}

export async function listProjects(status?: string): Promise<Project[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ projects: Project[] }>>('/v1/projects', {
    params: status ? { status } : {},
  });
  const body = unwrap(data);
  return Array.isArray(body?.projects) ? body.projects : [];
}

export async function createProject(req: { projectKey: string; name: string }): Promise<Project> {
  const { data } = await apiClient.post<ApiEnvelope<Project>>('/v1/projects', req,
    idempotentConfig());
  return unwrap(data);
}

export async function closeProject(projectId: string): Promise<Project> {
  const { data } = await apiClient.post<ApiEnvelope<Project>>(
    `/v1/projects/${encodeURIComponent(projectId)}/close`, undefined, idempotentConfig());
  return unwrap(data);
}

export async function listProjectMembers(projectId: string): Promise<ProjectMember[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ members: ProjectMember[] }>>(
    `/v1/projects/${encodeURIComponent(projectId)}/members`);
  const body = unwrap(data);
  return Array.isArray(body?.members) ? body.members : [];
}

export async function addProjectMember(projectId: string, req: {
  memberType: 'USER' | 'ROLE';
  member: string;
  projectRole?: string;
  expiresAt?: string | null;
}): Promise<ProjectMember> {
  const { data } = await apiClient.post<ApiEnvelope<ProjectMember>>(
    `/v1/projects/${encodeURIComponent(projectId)}/members`, req, idempotentConfig());
  return unwrap(data);
}

export async function revokeProjectMember(projectId: string, memberType: string,
                                          member: string): Promise<void> {
  await apiClient.delete(`/v1/projects/${encodeURIComponent(projectId)}/members`, {
    params: { memberType, member },
  });
}

/**
 * A project↔resource link (§4). `resourceType` is the closed UC1 ACL set (`work_item` | `flow` |
 * `entity` | `dataroom_invite`); unlink is an UPDATE row-flip (I7) — the row stays, with
 * `unlinkedBy`/`unlinkedAt` set.
 */
export interface ProjectResource {
  resourceType: string;
  resourceId: string;
  linkedBy: string;
  linkedAt: string | null;
  unlinkedBy: string | null;
  unlinkedAt: string | null;
}

export async function listProjectResources(projectId: string): Promise<ProjectResource[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ resources: ProjectResource[] }>>(
    `/v1/projects/${encodeURIComponent(projectId)}/resources`);
  const body = unwrap(data);
  return Array.isArray(body?.resources) ? body.resources : [];
}

export async function linkProjectResource(projectId: string, req: {
  resourceType: string;
  resourceId: string;
}): Promise<ProjectResource> {
  const { data } = await apiClient.post<ApiEnvelope<ProjectResource>>(
    `/v1/projects/${encodeURIComponent(projectId)}/resources`, req, idempotentConfig());
  return unwrap(data);
}

export async function unlinkProjectResource(projectId: string, resourceType: string,
                                            resourceId: string): Promise<void> {
  await apiClient.delete(`/v1/projects/${encodeURIComponent(projectId)}/resources`, {
    params: { resourceType, resourceId },
  });
}

// ---- delegations (§7) -------------------------------------------------------------------------

export interface Delegation {
  delegationId: string;
  delegateType: 'USER' | 'ROLE';
  delegate: string;
  scopeType: 'SPOKE' | 'PROJECT';
  scopeId: string;
  powers: string[];
  reasonCode: string;
  grantedBy: string;
  grantedAt: string | null;
  expiresAt: string | null;
  revokedBy: string | null;
  revokedAt: string | null;
}

export async function listDelegations(delegate?: string): Promise<Delegation[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ delegations: Delegation[] }>>(
    '/v1/delegations', { params: delegate ? { delegate } : {} });
  const body = unwrap(data);
  return Array.isArray(body?.delegations) ? body.delegations : [];
}

export async function createDelegation(req: {
  delegateType: 'USER' | 'ROLE';
  delegate: string;
  scopeType: 'SPOKE' | 'PROJECT';
  scopeId: string;
  powers: string[];
  reasonCode: string;
  expiresAt?: string | null;
}): Promise<Delegation> {
  const { data } = await apiClient.post<ApiEnvelope<Delegation>>('/v1/delegations', req,
    idempotentConfig());
  return unwrap(data);
}

export async function revokeDelegation(delegationId: string): Promise<Delegation> {
  const { data } = await apiClient.post<ApiEnvelope<Delegation>>(
    `/v1/delegations/${encodeURIComponent(delegationId)}/revoke`, undefined, idempotentConfig());
  return unwrap(data);
}

// ---- ABAC policies (§6) -----------------------------------------------------------------------

export interface AbacPolicy {
  policyId: string;
  policyCode: string;
  permissionKey: string;
  condition: unknown;
  version: number;
  note: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  createdBy: string;
  createdAt: string | null;
  publishedBy: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
}

export async function getPolicy(policyId: string): Promise<AbacPolicy> {
  const { data } = await apiClient.get<ApiEnvelope<AbacPolicy>>(
    `/v1/abac/policies/${encodeURIComponent(policyId)}`);
  return unwrap(data);
}

export async function listPolicies(status?: string): Promise<AbacPolicy[]> {
  const { data } = await apiClient.get<ApiEnvelope<{ policies: AbacPolicy[] }>>(
    '/v1/abac/policies', { params: status ? { status } : {} });
  const body = unwrap(data);
  return Array.isArray(body?.policies) ? body.policies : [];
}

export async function createPolicy(req: {
  policyCode: string;
  permissionKey: string;
  condition: unknown;
  note?: string | null;
}): Promise<AbacPolicy> {
  const { data } = await apiClient.post<ApiEnvelope<AbacPolicy>>('/v1/abac/policies', req,
    idempotentConfig());
  return unwrap(data);
}

export async function publishPolicy(policyId: string): Promise<AbacPolicy> {
  const { data } = await apiClient.post<ApiEnvelope<AbacPolicy>>(
    `/v1/abac/policies/${encodeURIComponent(policyId)}/publish`, undefined, idempotentConfig());
  return unwrap(data);
}

export async function archivePolicy(policyId: string): Promise<AbacPolicy> {
  const { data } = await apiClient.post<ApiEnvelope<AbacPolicy>>(
    `/v1/abac/policies/${encodeURIComponent(policyId)}/archive`, undefined, idempotentConfig());
  return unwrap(data);
}
